import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { LoadingBlock } from '../../../components/loading/loading-block.tsx';
import { CancellableSearchBox } from '../../../components/search/cancellable-search-box.tsx';
import { TableHeader } from '../../../components/tables/table-header.tsx';
import { CollapsibleContainer } from '../../../components/ui/collapsible-container.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import { ProductVariantSortBy } from '../../../server/dto/product-variant-dto.ts';
import type { RouterInput } from '../../../server/types/infer-trpc-types.ts';
import { SortOrder } from '../../../server/types/list-view-types.ts';
import { api } from '../../../utils/api.ts';
import { useDrawers } from '../../drawers/all-drawers.tsx';
import { DataTableSortHeader } from '../../list-views/components/data-table-sort-header.tsx';
import { DataTable } from '../../list-views/components/data-table.tsx';
import { useGenericListViewSorting } from '../../list-views/hooks/use-generic-list-view-sorting.tsx';
import { ProductQuickAdd } from '../../sales-draft-new/components/product-quick-add.tsx';
import { useDraftContext } from '../../sales-draft-new/hooks/use-draft-context.ts';
import InventoryDetailsQuickAdd from './inventory-details-quick-add.tsx';

type Props = {
    erpProductId: string;
    fetchEnabled: boolean;
    canAddToDraft?: boolean;
};

const columnHelper = createColumnHelper<ProductVariantDTO>();

export const InventoryDetailsVariants = ({ erpProductId, fetchEnabled, canAddToDraft }: Props) => {
    const { openProductDetails } = useDrawers();
    const enabled = fetchEnabled && Boolean(erpProductId);
    const [searchQuery, setSearchQuery] = useState('');
    const { draftId } = useDraftContext({ refetchOnMount: false });
    const { t } = useLocale();

    const [sortingFilter, setSortingFilter] = useState<Required<
        RouterInput['platoUser']['getDetailProduct']['getProductVariants']['sortBy']
    > | null>({
        field: ProductVariantSortBy.PRODUCT_VARIANT_ID,
        direction: SortOrder.ASC,
    });

    const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } =
        api.platoUser.getDetailProduct.getProductVariants.useInfiniteQuery(
            {
                erpProductId,
                searchQuery: searchQuery || undefined,
                sortBy: sortingFilter || undefined,
            },
            {
                enabled,
                keepPreviousData: true,
                getNextPageParam: (lastPage) => lastPage.nextCursor,
            },
        );

    const { data: countData, isLoading: isLoadingCount } =
        api.platoUser.getDetailProduct.getProductVariantsCount.useQuery(
            {
                erpProductId,
                searchQuery: searchQuery || undefined,
            },
            {
                enabled,
            },
        );

    const variants = useMemo(() => data?.pages.flatMap((page) => page.result) ?? [], [data]);

    const [sorting, sortChangedInInterface] = useGenericListViewSorting(
        sortingFilter,
        setSortingFilter,
        ProductVariantSortBy,
    );

    const columns = useMemo(() => {
        return [
            columnHelper.accessor('product_variant_id', {
                id: ProductVariantSortBy.PRODUCT_VARIANT_ID,
                enableSorting: true,
                header: ({ column }) => (
                    <DataTableSortHeader
                        column={column}
                        title={t('table_header__item_id', {
                            ns: 'product-details',
                        })}
                    />
                ),
                size: 50,
                cell: ({ cell }) => (
                    <LinkText
                        onClick={() => {
                            openProductDetails(cell.row.original.product_variant_id);
                        }}
                    >
                        {cell.getValue().toString()}
                    </LinkText>
                ),
            }),
            columnHelper.accessor('product_variant_name', {
                id: ProductVariantSortBy.PRODUCT_NAME,
                enableSorting: true,
                header: ({ column }) => (
                    <DataTableSortHeader
                        column={column}
                        title={t('table_header__item_name', {
                            ns: 'product-details',
                        })}
                    />
                ),
                size: 500,
                cell: ({ cell }) => <Text>{cell.getValue()}</Text>,
            }),
            canAddToDraft
                ? columnHelper.display({
                      id: 'quick_add',
                      header: () => null,
                      size: 200,
                      cell: ({ row }) => (
                          <div className="flex flex-row justify-end">
                              <InventoryDetailsQuickAdd product={row.original} variant="secondary" />
                          </div>
                      ),
                  })
                : draftId
                  ? columnHelper.display({
                        id: 'quick_add',
                        header: () => null,
                        size: 200,
                        cell: ({ row }) => (
                            <div className="flex flex-row justify-end">
                                <ProductQuickAdd product={row.original} variant="secondary" />
                            </div>
                        ),
                    })
                  : undefined,
        ].filter((x) => x !== undefined);
    }, [t, draftId, canAddToDraft, openProductDetails]);

    const table = useReactTable({
        data: variants,
        columns,
        state: { sorting },
        onSortingChange: sortChangedInInterface,
        defaultColumn: {
            minSize: 0,
            size: 0,
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (isLoading && variants.length === 0) {
        return <LoadingBlock />;
    }

    if (variants.length === 0 && !searchQuery) {
        return null;
    }

    return (
        <div className="flex flex-col">
            <CollapsibleContainer
                defaultOpen
                title={t('variants', {
                    ns: 'product-details',
                })}
                className="flex flex-col [&>div]:flex "
            >
                <div className="flex flex-col grow gap-3">
                    <CancellableSearchBox
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={t('search_placeholder', { ns: 'product-details' })}
                        className="w-56"
                    />
                    <DataTable
                        className="max-h-96"
                        isLoading={isLoading}
                        table={table}
                        isFetchingNextPage={isFetchingNextPage}
                        onFetchNextPage={fetchNextPage}
                        hasNextPage={hasNextPage}
                    />
                    <TableHeader count={countData?.count ?? 0} isLoadingCount={isLoadingCount} />
                </div>
            </CollapsibleContainer>
        </div>
    );
};
